"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useLayoutEffect } from "react";

import { useLocale, useTranslations } from "next-intl";

import { Feature } from "@/components/ui/Feature";
import { PropertyTable } from "@/components/ui/SearchTable/components/PropertyTable";
import { titleToSlug } from "@/data/helper/titleToSlug";
import { DealType } from "@/enums/dealType";
import { FeatureFlags } from "@/enums/feature-flags";
import { EnumViewMode } from "@/enums/view-mode";
import { useIsGoldenVisaEligible } from "@/hooks/useIsGoldenVisaEligible";
import { useUnifiedListing } from "@/hooks/useUnifiedListing";
import { DevelopersApiResponse } from "@/types/property.types";
import { EmptyState } from "@/ui/Common/EmptyState";
import Loader from "@/ui/Common/Loader";
import { ObjectCard } from "@/ui/Common/ObjectCard";
import { ObjectList } from "@/ui/Common/ObjectList";
import { PaginationContainer } from "@/ui/Common/Pagination";
import { EnhancedSearchPanel } from "@/ui/EnhancedSearchPanel";
import { SearchResultHeader } from "@/ui/SearchResultHeader";
import { restoreScroll } from "@/utils/scrollRestoration";
import { useUserRole } from "@/hooks/useUserRole";

interface Props {
  values: DevelopersApiResponse;
  h1?: string;
}

export default function DevelopersPage({ values, h1 }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAvalibleVisa = useIsGoldenVisaEligible();
  const role = useUserRole()

  // Use unified listing hook with all the logic consolidated
  const {
    data,
    transformedData,
    isFetching,
    isInitializing,
    currentPage,
    limit,
    total,
    totalPage,
    setPage,
    setPageSize,
    isEmpty,
    viewMode,
    isRealtor,
    initialFilters,
    localizedLocationString,
  } = useUnifiedListing({
    dealType: DealType.PRIMARY,
    basePath: "/developers",
    values,
  });

  console.log("PROPP", data, transformedData);

  const isLoading = isFetching || !data || isInitializing;

  useLayoutEffect(() => {
    restoreScroll(DealType.PRIMARY);
  }, []);

  return (
    <section className="min-h-screen">
      <EnhancedSearchPanel
        dealType={DealType.PRIMARY}
        initialFilters={initialFilters}
        header={h1 || t("Developers")}
        localizedLocationString={isLoading ? " " : localizedLocationString}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <SearchResultHeader
            total={total}
            dealType={DealType.PRIMARY}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />

          <Feature name={FeatureFlags.PROPERTIES_LIST_TABLE_VIEW}>
            <Feature.Enabled>
              {isRealtor && viewMode === EnumViewMode.TABLE ? (
                <PropertyTable
                  properties={transformedData}
                  loading={isFetching}
                  currentSort="date_desc"
                  onSortChange={() => {}}
                  pagination={{
                    currentPage,
                    pageSize: limit,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: () => {}, // managed by header
                  }}
                  enableBulkActions={true}
                  hideInternalPagination={true}
                />
              ) : (
                <ObjectList isEmpty={isEmpty}>
                  <ObjectList.Items viewMode={viewMode}>
                    {transformedData.map((item) => (
                      <ObjectCard
                        key={item.id ?? item.title}
                        id={item.id}
                        title={item.title ?? "N/A"}
                        location={item.location ?? item.city ?? "N/A"}
                        slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                        view={viewMode}
                        translations={item.translations}
                        isAvalibleVisa={isAvalibleVisa}
                        canArchive={role.isRealtor}
                        {...item}
                      />
                    ))}
                  </ObjectList.Items>
                  <ObjectList.Empty>
                    <EmptyState />
                  </ObjectList.Empty>
                </ObjectList>
              )}
            </Feature.Enabled>
            <Feature.Disabled>
              <ObjectList isEmpty={isEmpty}>
                <ObjectList.Items viewMode={viewMode}>
                  {transformedData.map((item) => (
                    <ObjectCard
                      key={item.id ?? item.title}
                      id={item.id}
                      title={item.title ?? "N/A"}
                      location={item.location ?? item.city ?? "N/A"}
                      slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                      view={viewMode}
                      translations={item.translations}
                      isAvalibleVisa={isAvalibleVisa}
                      canArchive={role.isRealtor}
                      {...item}
                    />
                  ))}
                </ObjectList.Items>
                <ObjectList.Empty>
                  <EmptyState />
                </ObjectList.Empty>
              </ObjectList>
            </Feature.Disabled>
          </Feature>

          {/* Pagination for multiple pages */}
          {totalPage > 1 && (
            <PaginationContainer
              totalPage={totalPage}
              currentPage={currentPage}
              pathname={pathname}
              searchParams={searchParams}
            />
          )}
        </>
      )}
    </section>
  );
}
